import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import ParticleBackground from './ParticleBackground';
import logo from '../logo.svg';
import SEO from './SEO';

const animations = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-up {
    animation: fadeIn 1.5s ease-out forwards;
  }
  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  .animate-slide-in {
    animation: slideIn 0.3s ease-out forwards;
  }
  @keyframes slideOut {
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
  }
  .animate-slide-out {
    animation: slideOut 0.3s ease-out forwards;
  }
`;

interface HeroSectionProps {
  // handleCustomerPortal: () => void; // Removed prop
}

const HeroSection: React.FC<HeroSectionProps> = (/*{ handleCustomerPortal }*/) => { // Removed prop
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const navigateToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = 100;
      const elementPosition = section.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handlePricingLink = () => {
    setIsMobileMenuOpen(false);
  };

  // Removed handleManageSubscriptionClick function

  return (
    <>
      <SEO
        title="AISS | Web Dev & Automation Solutions"
        description="AISS empowers entrepreneurs with enterprise-level web development, automation, and customer journey solutions. Book your free discovery call today."
        url="https://aiss.solutions/"
        image="https://aiss.solutions/images/og-share.jpg"
      />
      <div className="relative min-h-[100vh] bg-gradient-to-br from-blue-950 to-blue-800 overflow-hidden">
        <ParticleBackground />

        {/* Header with Logo and Navigation */}
        <header className="w-full py-12 px-8 md:px-12 lg:px-16">
          <div className="max-w-[90rem] mx-auto flex justify-between items-center">
            <div className="relative animate-fade-in">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                <img
                  src={logo}
                  alt="AISS Logo"
                  className="w-32 md:w-36 transition-all duration-300 hover:scale-105 filter drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 hover:opacity-20 transition-opacity duration-300 rounded-lg" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => navigateToSection('our-work')} className="text-blue-200 hover:text-white transition-colors duration-200 font-medium">Projects</button>
              <button onClick={() => navigateToSection('about')} className="text-blue-200 hover:text-white transition-colors duration-200 font-medium">About</button>
              <Link to="/subscriptions" className="text-blue-200 hover:text-white transition-colors duration-200 font-medium">Pricing</Link>
              <button onClick={() => navigateToSection('contact')} className="text-blue-200 hover:text-white transition-colors duration-200 font-medium">Contact</button>
              {/* Removed Manage Subscription button from desktop nav */}
            </nav>

            {/* Mobile Menu Icon */}
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-blue-200 hover:text-white focus:outline-none">
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Click-away overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-0 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div
              className="md:hidden fixed top-0 right-0 h-full w-3/4 max-w-sm bg-slate-900 bg-opacity-90 backdrop-blur-md shadow-xl z-50 p-8 pt-24 flex flex-col space-y-6 animate-slide-in"
            >
              <button onClick={() => navigateToSection('our-work')} className="text-blue-100 hover:text-white transition-colors duration-200 font-medium text-lg py-2 text-left">Projects</button>
              <button onClick={() => navigateToSection('about')} className="text-blue-100 hover:text-white transition-colors duration-200 font-medium text-lg py-2 text-left">About</button>
              <Link to="/subscriptions" onClick={handlePricingLink} className="text-blue-100 hover:text-white transition-colors duration-200 font-medium text-lg py-2 text-left">Pricing</Link>
              <button onClick={() => navigateToSection('contact')} className="text-blue-100 hover:text-white transition-colors duration-200 font-medium text-lg py-2 text-left">Contact</button>
              {/* Removed Manage Subscription button from mobile nav */}
            </div>
          </>
        )}

        {/* Main content */}
        <div className="relative max-w-[90rem] mx-auto px-4 pt-24 pb-36 sm:pb-24 text-center">
          <div className={`${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'} transition-all duration-1000`}>
            <h1 className="text-5xl md:text-7xl font-bold text-white">
              Build Faster Websites. Automate Customer Journeys. Win More Business.
            </h1>
            <p className="text-blue-300 text-2xl mt-4">
              Helping businesses scale smarter, not harder.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 mt-8">
              <button
                onClick={() => {
                  if (window.Calendly) {
                    window.Calendly.initPopupWidget({ url: 'https://calendly.com/nicksolo96/30min' });
                  } else {
                    console.warn('Calendly widget not loaded');
                  }
                }}
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
              >
                Book a Free Discovery Call
              </button>
              <button
                onClick={() => navigateToSection('our-work')}
                className="px-8 py-4 border border-blue-400 text-blue-100 hover:bg-blue-800 rounded-lg font-semibold flex items-center justify-center"
              >
                View My Work
                <ArrowUpRight className="ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        {!isMobileMenuOpen && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
            <div className="w-4 h-6 border-2 border-blue-300 rounded-full flex justify-center mb-2">
              <div className="w-1 h-2 bg-blue-300 rounded-full mt-1 animate-bounce"></div>
            </div>
            <span className="text-blue-200 text-xs">Scroll to explore</span>
          </div>
        )}

        <style dangerouslySetInnerHTML={{ __html: animations }} />
      </div>
    </>
  );
};

export default HeroSection;